import type {
  BusinessAnalysisAoi,
  BusinessAnalysisRadiusKey,
  BusinessAnalysisRadiusResult,
  BusinessAnalysisScore,
  BusinessAnalysisSnapshot
} from "../../shared/types";

export type CommercialWeightKey = Extract<
  BusinessAnalysisScore["key"],
  "food" | "snacksBeverage" | "tobacco" | "cigarettes" | "cannabis" | "fuel"
>;
export type DemographicWeightKey = Extract<
  BusinessAnalysisScore["key"],
  "growth" | "residentialMarket" | "daytimeMarket" | "ageProfile"
>;
export type ScenarioWeightKey = CommercialWeightKey | DemographicWeightKey;
export type BusinessAnalysisWeightScenario = Record<ScenarioWeightKey, number>;

export const COMMERCIAL_WEIGHT_KEYS: CommercialWeightKey[] = ["food", "snacksBeverage", "tobacco", "cigarettes", "cannabis", "fuel"];
export const DEMOGRAPHIC_WEIGHT_KEYS: DemographicWeightKey[] = ["growth", "residentialMarket", "daytimeMarket", "ageProfile"];
export const TOTAL_OPPORTUNITY_WEIGHT = 100;
export const DEFAULT_WEIGHT_SCENARIO: BusinessAnalysisWeightScenario = {
  growth: 10,
  residentialMarket: 15,
  daytimeMarket: 10,
  ageProfile: 5,
  food: 10,
  snacksBeverage: 10,
  tobacco: 5,
  cigarettes: 5,
  cannabis: 5,
  fuel: 25
};

const radiusWeights: Record<BusinessAnalysisRadiusKey, number> = { R05: 0.25, R10: 0.35, R20: 0.4 };

function round(value: number, digits = 0): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function grade(score: number): BusinessAnalysisRadiusResult["grade"] {
  if (score >= 80) return "Excellent";
  if (score >= 65) return "Strong";
  if (score >= 50) return "Moderate";
  return "Caution";
}

function recommendation(score: number): BusinessAnalysisAoi["recommendation"] {
  if (score >= 80) return "Prioritize";
  if (score >= 65) return "Advance with validation";
  if (score >= 50) return "Monitor";
  return "Do not prioritize";
}

export function weightGroupTotal(scenario: BusinessAnalysisWeightScenario, keys: ScenarioWeightKey[]): number {
  return round(keys.reduce((total, key) => total + scenario[key], 0), 1);
}

export function isDefaultWeightScenario(scenario: BusinessAnalysisWeightScenario): boolean {
  return (Object.keys(DEFAULT_WEIGHT_SCENARIO) as ScenarioWeightKey[])
    .every((key) => scenario[key] === DEFAULT_WEIGHT_SCENARIO[key]);
}

export function scaleWeightGroup(
  scenario: BusinessAnalysisWeightScenario,
  keys: ScenarioWeightKey[],
  targetTotal: number
): BusinessAnalysisWeightScenario {
  const next = { ...scenario };
  const safeTarget = round(Math.max(0, targetTotal), 1);
  const currentTotal = weightGroupTotal(scenario, keys);
  let allocated = 0;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      next[key] = round(safeTarget - allocated, 1);
      return;
    }
    const share = currentTotal > 0 ? scenario[key] / currentTotal : 1 / keys.length;
    next[key] = round(safeTarget * share, 1);
    allocated += next[key];
  });
  return next;
}

export function redistributeGroupWeight(
  scenario: BusinessAnalysisWeightScenario,
  keys: ScenarioWeightKey[],
  changedKey: ScenarioWeightKey,
  requestedWeight: number
): BusinessAnalysisWeightScenario {
  const groupTotal = weightGroupTotal(scenario, keys);
  const nextWeight = round(Math.max(0, Math.min(groupTotal, requestedWeight || 0)), 1);
  const otherKeys = keys.filter((key) => key !== changedKey);
  const otherTotal = weightGroupTotal(scenario, otherKeys);
  const remaining = round(groupTotal - nextWeight, 1);
  const next = { ...scenario, [changedKey]: nextWeight };
  let allocated = 0;

  otherKeys.forEach((key, index) => {
    if (index === otherKeys.length - 1) {
      next[key] = round(remaining - allocated, 1);
      return;
    }
    const share = otherTotal > 0 ? scenario[key] / otherTotal : 1 / otherKeys.length;
    next[key] = round(remaining * share, 1);
    allocated += next[key];
  });
  return next;
}

export function recalculateBusinessAnalysis(
  snapshot: BusinessAnalysisSnapshot,
  scenario: BusinessAnalysisWeightScenario
): BusinessAnalysisSnapshot {
  if (isDefaultWeightScenario(scenario)) return snapshot;

  const aois = snapshot.aois.map((aoi): BusinessAnalysisAoi => {
    const results = aoi.results.map((result): BusinessAnalysisRadiusResult => {
      const scores = result.scores.map((score) => {
        const customWeight = scenario[score.key as ScenarioWeightKey];
        return customWeight === undefined ? score : { ...score, weight: customWeight };
      });
      const opportunityScore = round(scores.reduce((total, score) => total + score.score * score.weight, 0) / TOTAL_OPPORTUNITY_WEIGHT, 1);
      const suitabilityScore = round(opportunityScore * 0.75 + result.stationFitScore * 0.25, 1);
      return { ...result, scores, opportunityScore, suitabilityScore, grade: grade(suitabilityScore) };
    });
    const overallScore = round(results.reduce(
      (total, result) => total + result.suitabilityScore * radiusWeights[result.radiusKey],
      0
    ), 1);
    return { ...aoi, results, overallScore, recommendation: recommendation(overallScore) };
  }).sort((left, right) => right.overallScore - left.overallScore);

  return { ...snapshot, aois };
}
